import clsx from "clsx";
import { Dialog as HeadlessDialog } from "@headlessui/react";

import { View, Text } from ".";
import { Icon } from "./Icon";
import { IconButton } from "./IconButton";

type DialogProps = {
  children: React.ReactNode;
  className?: string;
  isOpen: boolean;
  onClose: () => void;
  title?: string | React.ReactNode;
  size?: "sm" | "md" | "lg" | "flex";
  headerActions?: React.ReactNode;
  actions?: React.ReactNode;
  /** If provided, shows Cancel + Save buttons in footer */
  onSave?: () => void;
  /** Label for save button. Defaults to "Save" */
  saveLabel?: string;
  /** Label for cancel button. Defaults to "Cancel" */
  cancelLabel?: string;
  /** Loading state for save button */
  isSaving?: boolean;
};

export const Dialog = ({
  children,
  className,
  isOpen,
  onClose,
  title,
  size = "md",
  headerActions,
  actions,
  onSave,
  saveLabel = "Save",
  cancelLabel = "Cancel",
  isSaving = false,
}: DialogProps) => {
  return (
    <HeadlessDialog
      open={isOpen}
      onClose={onClose}
      className="relative z-50"
    >
      <View
        className="fixed inset-0 bg-black/30 backdrop-blur-sm"
        aria-hidden="true"
      />

      <View className="fixed inset-0 flex items-center justify-center p-4">
        <HeadlessDialog.Panel
          className={clsx(
            "rounded-xl",
            "flex flex-col",
            "bg-white dark:bg-gray-800",
            "shadow-xl border border-gray-200 dark:border-gray-700",
            {
              "w-full max-w-sm": size === "sm",
              "w-full max-w-md": size === "md",
              "w-full max-w-lg": size === "lg",
              "w-auto": size === "flex",
            },
            className,
          )}
        >
          {title && (
            <View className="flex items-start justify-between gap-3 p-4 border-b border-gray-200 dark:border-gray-700">
              <HeadlessDialog.Title className="flex-1 min-w-0">
                {typeof title === "string" ? <Text className="text-lg font-semibold">{title}</Text> : title}
              </HeadlessDialog.Title>
              <View className="flex flex-row items-center gap-2">
                {headerActions}
                <IconButton
                  variant="info"
                  icon="close"
                  onClick={onClose}
                />
              </View>
            </View>
          )}
          {children}
          {actions ? (
            <View className="flex items-center justify-end p-4 gap-3 border-t border-gray-200 dark:border-gray-700">{actions}</View>
          ) : onSave ? (
            <View className="flex items-center justify-end p-4 gap-3 border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={onClose}
                className="flex flex-row items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-sm font-medium transition-colors border bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                {cancelLabel}
              </button>
              <button
                type="button"
                onClick={onSave}
                disabled={isSaving}
                className="flex flex-row items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-sm font-medium transition-colors border bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800 hover:bg-emerald-100 dark:hover:bg-emerald-900/50 disabled:opacity-50"
              >
                {isSaving ? (
                  <Icon
                    name="loader"
                    size={14}
                    className="animate-spin"
                  />
                ) : (
                  <Icon
                    name="check"
                    size={14}
                  />
                )}
                {saveLabel}
              </button>
            </View>
          ) : null}
        </HeadlessDialog.Panel>
      </View>
    </HeadlessDialog>
  );
};
